import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  describeAcceptance,
  type ReportAcceptanceInput,
  type ReportAcceptanceState,
} from '../_lib/reportAcceptance';

/**
 * Lifecycle chip for a report — used by BOTH the queue list and the detail
 * drawer header (AUT-284).
 *
 * Replaces `ReportStatusBadge`, which rendered the raw `Report.status`
 * enum. That enum is NEVER advanced on acceptance (see
 * `packages/api/src/modules/reports/report-status.ts`) — a report rests at
 * `submitted` whether the customer accepted it or not — so the list
 * presented settled, paid-out, released reports as «ждёт заказчика»: an
 * operator queue of decisions nobody owed.
 *
 * The chip is therefore derived from `describeAcceptance`, the SAME pure
 * read-out the drawer's `ReportAcceptanceBar` uses. One truth, one
 * derivation: a second one is how the two surfaces disagreed in the first
 * place. `acceptedAt` is the real signal; `status` only decides between
 * draft / legacy-moderation / awaiting.
 *
 * Typed as a total `Record<ReportAcceptanceState, …>` so a new state in
 * `reportAcceptance.ts` fails typecheck here instead of rendering an
 * uncoloured chip at runtime.
 *
 * Colours:
 *   - accepted (any flavour) → success — settled, nothing owed
 *   - awaitingCustomer       → info    — the resting state of a finished
 *     report while the customer decides; no admin can act on it, so
 *     informational blue rather than a warning amber nag
 *   - draft                  → neutral — inspector still filling it in
 *   - legacy*                → their historical colour, labelled «архив»
 *     so nobody reads «одобрен» as something an admin did recently
 */
const STATE_COLOR: Record<ReportAcceptanceState, ChipProps['color']> = {
  acceptedByCustomer: 'success',
  acceptedAuto: 'success',
  accepted: 'success',
  awaitingCustomer: 'info',
  draft: 'default',
  legacyApproved: 'success',
  legacyRejected: 'error',
  legacyRevisionRequested: 'default',
};

export const ReportAcceptanceBadge = ({
  report,
}: {
  /** A list row OR a detail DTO — both satisfy `ReportAcceptanceInput`. */
  report: ReportAcceptanceInput;
}): ReactElement => {
  const { t } = useTranslation();
  const { state } = describeAcceptance(report);
  return (
    <Chip size="small" color={STATE_COLOR[state]} label={t(`reports.acceptanceBadge.${state}`)} />
  );
};
